// Payment instrument capture (design §0, §3.3, §5.2, D6).
//
// THE GOVERNING RULE: the storefront COLLECTS payment information and NEVER
// PROCESSES it. This module posts what the customer typed to our own service so
// it can be encrypted and recorded (§5). It contacts no gateway, no bank and no
// card network, performs no authorization or tokenization, and there is no SDK
// here to swap one in.
//
// WHAT THIS REPLACES. The previous form fabricated a `devtok_<method>_<last4>_
// <timestamp>` string from a timestamp, submitted it as `payment_token`, and
// threw the actual card and bank details away — and the server stored that
// string as the processor reference. So the system recorded a payment intent
// that referenced NOTHING, and MIS could not have billed from it (finding F1).
// The doc-comment describing that placeholder as a swap-point for "the Aldelo
// Pay tokenization SDK" pointed at an integration that will never arrive:
// Aldelo Pay was cancelled and decommissioned under TD-373 (F11).
//
// The reference returned here is a real server-issued handle to a real stored
// instrument, and it travels to order-create as `payment_instrument_id`.
//
// ─────────────────────────────────────────────────────────────────────────────
// CONTRACT ALIGNED (TD-461, 2026-09-09) — these field names are the Go lane's,
// read from customer_handler_payment_instruments.go: `billing_address_id`
// (required — the geo authority the server derives the method from),
// `name_on_card` (card; the old `cardholder_name` was never read server-side),
// ACH names unchanged. The order-create body takes this response's `id` as
// `payment_instrument_id`; the legacy `payment_token` field is accepted and
// IGNORED by the server and is no longer sent.
// ─────────────────────────────────────────────────────────────────────────────
//
// CVV IS NOT COLLECTED and there is no field for one (§3.4). A CVV cannot be
// retained after authorization, and this system performs no authorization: it
// records an instrument that MIS bills later, asynchronously. A captured CVV
// would have nothing to be spent on before it had to be destroyed. If MIS's
// first authorization turns out to need one (open question OQ-3), that is a
// synchronous flow needing its own design — not a field added here.

import { embedApiFetch } from "../../embed-shared/api-client";
import { idempotencyHeaders } from "../../embed-shared/idempotency-errors";
import type { AchAccountType, CardBrand } from "./payment-format";

/** The Go lane's route (TD-461-aligned). */
const PAYMENT_INSTRUMENTS_PATH = "/api/v1/marketplace/me/payment-instruments";

/**
 * ACH capture. Routing and account numbers are SENSITIVE and are encrypted
 * server-side (§5.2); the holder name and account type are display-safe and
 * stored in clear.
 */
export interface AchCaptureRequest {
  /** Union discriminant for THIS client only — the server ignores it and
   *  derives the method from the billing address's country (§3.2). */
  payment_method: "ach";
  /** The order's billing address row — the geo authority (§1.1, TD-461). */
  billing_address_id: number;
  account_holder_name: string;
  routing_number: string;
  account_number: string;
  account_type: AchAccountType;
}

/**
 * Card capture. The number and expiry are SENSITIVE and are encrypted
 * server-side (§5.2). The brand is DERIVED from the number, never asked (§3.5),
 * and is re-derived server-side — the copy sent here is a client convenience
 * the server must not trust.
 */
export interface CardCaptureRequest {
  /** Union discriminant for THIS client only — the server ignores it and
   *  derives the method from the billing address's country (§3.2). */
  payment_method: "card";
  /** The order's billing address row — the geo authority (§1.1, TD-461). */
  billing_address_id: number;
  /** The Go handler's field name — `cardholder_name` was never read (TD-461). */
  name_on_card: string;
  card_number: string;
  expiry_month: number;
  expiry_year: number;
}

export type PaymentInstrumentCaptureRequest = AchCaptureRequest | CardCaptureRequest;

/**
 * The recorded instrument, as the customer may see it.
 *
 * Only display-safe values come back: a reference, the method, the derived
 * brand and the last four digits. Nothing returned here can reconstruct the
 * instrument, which is what lets the review step and the portal render an order
 * summary without a KMS decrypt (§5.2, §5.4).
 */
export interface PaymentInstrumentWire {
  /** Server-issued handle. Opaque — never parsed, never displayed. */
  id: string;
  payment_method: "ach" | "card";
  /** Derived server-side from the card number. Absent for ACH. */
  brand?: CardBrand;
  /** Last four digits, display-safe. */
  last4: string;
}

/**
 * Record a captured instrument and return its reference.
 *
 * Ordering note (D6): "enter payment info → validate format only → record,
 * encrypted → customer ticks acceptance → submit order". This call is the
 * record step, so it runs at the end of the payment step and before the order
 * is submitted — never as part of order submission, which would put a raw card
 * number in the order-create body.
 *
 * Idempotency (rule 79): the route IS keyed — `withAreaIdem` wraps it in
 * RequireKey, which 400s IDEMPOTENCY_KEY_REQUIRED on a missing header — so this
 * call attaches a FRESH key per submission (`idempotencyHeaders()`), like every
 * other keyed marketplace mutation. Still open, but OQ-4's half rather than the
 * route's: a customer who deliberately steps back and submits again mints a NEW
 * key, so a second unreferenced instrument can still be recorded — the
 * retention sweep's job to clear.
 */
export async function capturePaymentInstrument(
  req: PaymentInstrumentCaptureRequest,
): Promise<PaymentInstrumentWire> {
  return embedApiFetch<PaymentInstrumentWire>(PAYMENT_INSTRUMENTS_PATH, {
    method: "POST",
    body: req,
    headers: idempotencyHeaders(),
  });
}
